import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";

const WHATSAPP_NUMBER = "393935817177";

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || "w-7 h-7"}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const WhatsAppChat: React.FC = () => {
  const { t } = useLanguage();
  const wa = t.whatsappChat;
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [showPulse, setShowPulse] = useState(true);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Hide pulse after first interaction
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const handleSend = () => {
    const text = message.trim() || wa.defaultMessage;
    const encoded = encodeURIComponent(text);
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`,
      "_blank",
      "noopener,noreferrer",
    );
    setMessage("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickMessage = (msg: string) => {
    const encoded = encodeURIComponent(msg);
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`,
      "_blank",
      "noopener,noreferrer",
    );
    setIsOpen(false);
  };

  return (
    <div
      ref={chatRef}
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end"
    >
      {/* Chat Popup */}
      {isOpen && (
        <div
          className="mb-4 w-[350px] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 border border-gray-100"
          style={{
            animation: "slideUp 0.3s ease-out",
          }}
        >
          {/* Header */}
          <div className="bg-[#075E54] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo.png"
                  alt="Ca Pe Vremuri"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    (e.target as HTMLImageElement).parentElement!.innerHTML =
                      '<span class="text-white font-bold text-sm">CPV</span>';
                  }}
                />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">
                  Ca Pe Vremuri
                </h3>
                <div className="flex items-center space-x-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full inline-block" />
                  <span className="text-green-200 text-xs">{wa.online}</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Chat Body */}
          <div className="bg-[#ECE5DD] p-4 min-h-[160px]">
            {/* Welcome Bubble */}
            <div className="bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-sm max-w-[85%]">
              <p className="text-gray-800 text-sm leading-relaxed">
                {wa.welcomeMessage}
              </p>
              <span className="text-[10px] text-gray-400 mt-1 block text-right">
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            {/* Quick Reply Buttons */}
            <div className="mt-3 flex flex-wrap gap-2">
              {wa.quickReplies.map(
                (reply: { label: string; message: string }, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickMessage(reply.message)}
                    className="bg-white text-[#075E54] text-xs px-3 py-2 rounded-full border border-[#075E54]/20 hover:bg-[#075E54] hover:text-white transition-all duration-200 shadow-sm"
                  >
                    {reply.label}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Input Area */}
          <div className="px-3 py-3 bg-[#F0F0F0] flex items-end space-x-2">
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={wa.inputPlaceholder}
              rows={1}
              className="flex-1 resize-none bg-white rounded-full px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 border-0 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50 max-h-24"
              style={{ minHeight: "40px" }}
            />
            <button
              onClick={handleSend}
              className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:bg-[#1DA851] transition-colors flex-shrink-0 shadow-md"
              aria-label={wa.send}
            >
              <SendIcon />
            </button>
          </div>

          {/* Footer note */}
          <div className="px-4 py-2 bg-[#F0F0F0] border-t border-gray-200">
            <p className="text-[10px] text-gray-400 text-center">
              {wa.poweredBy}
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setShowPulse(false);
        }}
        className={`
          group relative w-[60px] h-[60px] rounded-full shadow-lg
          flex items-center justify-center
          transition-all duration-300 ease-in-out
          ${
            isOpen
              ? "bg-gray-600 hover:bg-gray-700 rotate-0"
              : "bg-[#25D366] hover:bg-[#1DA851] hover:scale-110"
          }
        `}
        aria-label={isOpen ? wa.close : wa.open}
      >
        {/* Pulse animation */}
        {showPulse && !isOpen && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-pulse opacity-20" />
          </>
        )}

        {isOpen ? (
          <CloseIcon />
        ) : (
          <WhatsAppIcon className="w-7 h-7 text-white" />
        )}

        {/* Tooltip */}
        {!isOpen && (
          <span className="absolute right-full mr-3 bg-gray-800 text-white text-xs py-2 px-3 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
            {wa.tooltip}
            <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-gray-800 rotate-45" />
          </span>
        )}
      </button>

      {/* CSS Animation */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(16px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppChat;
